/**
 * Bambuser live shopping. The embed is injected by GTM behind a consent trigger;
 * this is the theme's side: player cart/hydration, and the accessibility fallback
 * button that re-opens the widget when UsableNet accessibility mode hides it.
 *
 * Both Bambuser callbacks are defined by an inline stub in 3p-bambuser.liquid so
 * they exist before embed.js loads; this file drains what the stub queued.
 *
 * Dismissal is keyed on showId, so it sticks for one show but a newly published
 * show reappears. The GTM tag's '_bamls_dfw' reset keeps Bambuser's own session
 * dismissal from competing — leave it in place.
 */
(function () {
  'use strict';

  // SET TO false BEFORE DEPLOYING TO PRODUCTION.
  var DEBUG = true;

  function debug() {
    if (!DEBUG) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[bambuser]');
    console.log.apply(console, args);
  }

  var MAX_IMAGES_COUNT = 6;
  var PRODUCT_HANDLE_REGEX = /\/products\/([\w\d\-+]+)/;
  var A11Y_ACTIVE_TEXT = 'Exit Accessibility Mode';
  var A11Y_INACTIVE_TEXT = 'Enable Accessibility';
  var DISMISSED_SHOW_KEY = 'bambuserDismissedShow';

  // ---------------------------------------------------------------------------
  // Shopify
  // ---------------------------------------------------------------------------

  function sanitizeUrl(url) {
    if (typeof url !== 'string') return null;
    if (url.startsWith('//')) return 'https:' + url;
    if (url.toLowerCase().startsWith('http')) return url;
    return null;
  }

  function getActiveCurrency() {
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      return window.Shopify.currency.active;
    }
    var fromCookie = document.cookie
      .split('; ')
      .find(function (row) { return row.startsWith('cart_currency'); });
    return fromCookie ? fromCookie.split('=')[1] : null;
  }

  function asJson(response) {
    return response.json().then(function (body) {
      return { ok: response.ok, status: response.status, body: body };
    });
  }

  var storeApi = {
    getProductByUrl: function (url) {
      var match = PRODUCT_HANDLE_REGEX.exec(url);
      var handle = match && match[1];
      if (!handle) return Promise.reject(new Error('No product handle in URL: ' + url));
      return fetch('/products/' + handle + '.js', {
        headers: { 'Content-Type': 'application/json' },
      }).then(function (res) {
        if (!res.ok) throw new Error('Product fetch failed (' + res.status + '): ' + url);
        return res.json();
      });
    },

    addToCart: function (variantId) {
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ quantity: 1, id: variantId }] }),
      }).then(asJson);
    },

    updateItemInCart: function (variantId, quantity) {
      var updates = {};
      updates[variantId] = quantity;
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: updates }),
      }).then(asJson);
    },

    getCartState: function () {
      return fetch('/cart.js', {
        headers: { 'Content-Type': 'application/json' },
      }).then(function (res) { return res.json(); });
    },

    // Variant ids are not a fixed length; match on shape.
    getVariantIdFromUrl: function (url) {
      try {
        var variantId = new URL(url, window.location.origin).searchParams.get('variant');
        return variantId && /^\d+$/.test(variantId) ? variantId : null;
      } catch (error) {
        return null;
      }
    },
  };

  // ---------------------------------------------------------------------------
  // Player
  // ---------------------------------------------------------------------------

  function bindCartHandlers(player) {
    player.on(player.EVENT.ADD_TO_CART, function (addedItem, callback) {
      debug('ADD_TO_CART', addedItem);
      storeApi
        .addToCart(addedItem.sku)
        .then(function (res) {
          debug('ADD_TO_CART result', res.status, res.body);
          if (res.ok && res.body.items) return callback(true);
          if (res.status === 422) return callback({ success: false, reason: 'out-of-stock' });
          callback(false);
        })
        .catch(function (error) {
          console.error('[bambuser] Add to cart failed', error);
          callback(false);
        });
    });

    player.on(player.EVENT.UPDATE_ITEM_IN_CART, function (updatedItem, callback) {
      debug('UPDATE_ITEM_IN_CART', updatedItem);
      storeApi
        .updateItemInCart(updatedItem.sku, updatedItem.quantity)
        .then(function (res) {
          debug('UPDATE_ITEM_IN_CART result', res.status, res.body);
          callback(Boolean(res.ok && res.body.items));
        })
        .catch(function (error) {
          console.error('[bambuser] Cart update failed', error);
          callback(false);
        });
    });

    player.on(player.EVENT.SYNC_CART_STATE, function () {
      storeApi
        .getCartState()
        .then(function (cart) {
          debug('SYNC_CART_STATE, item_count =', cart.item_count);
          if (cart.item_count === 0) player.updateCart({ items: [] });
        })
        .catch(function (error) {
          console.error('[bambuser] Cart sync failed', error);
        });
    });

    player.on(player.EVENT.CHECKOUT, function () {
      debug('CHECKOUT -> /cart');
      player.showCheckout(window.location.origin + '/cart');
    });
  }

  function bindProductHydration(player) {
    player.on(player.EVENT.PROVIDE_PRODUCT_DATA, function (event) {
      debug('PROVIDE_PRODUCT_DATA for', event.products.length, 'product(s)');
      event.products.forEach(function (entry) {
        var variantId = storeApi.getVariantIdFromUrl(entry.url);
        debug('hydrating', entry.id, entry.url, 'variant:', variantId);

        storeApi
          .getProductByUrl(entry.url)
          .then(function (item) {
            player.updateProduct(entry.id, function (productFactory) {
              return productFactory.product(function (detailsFactory) {
                return detailsFactory
                  .name(item.title)
                  .sku(item.id)
                  .brandName(item.vendor)
                  .description(item.description)
                  .defaultVariationIndex(
                    variantId
                      ? Math.max(0, item.variants.findIndex(function (v) { return String(v.id) === String(variantId); }))
                      : 0
                  )
                  .variations(function (variationFactory) {
                    return item.variants.map(function (variation) {
                      return variationFactory()
                        .attributes(function (attributeFactory) {
                          return attributeFactory.colorName(variation.title);
                        })
                        .imageUrls(
                          (variation.featured_image ? [variation.featured_image.src] : [])
                            .concat(item.images.slice(0, MAX_IMAGES_COUNT - 1))
                            .map(sanitizeUrl)
                            .filter(Boolean)
                        )
                        .name(variation.title)
                        .sku(item.id)
                        .sizes(function (sizeFactory) {
                          return [
                            sizeFactory()
                              .name(variation.title)
                              .sku(variation.id)
                              .inStock(variation.available)
                              .price(function (priceFactory) {
                                // compare_at_price is null with no sale; null/100 is 0.
                                var current = variation.price / 100;
                                var original = variation.compare_at_price
                                  ? variation.compare_at_price / 100
                                  : current;
                                return priceFactory.original(original).current(current);
                              }),
                          ];
                        });
                    });
                  });
              });
            });
          })
          .then(function () { debug('hydrated', entry.id); })
          .catch(function (error) {
            console.error('[bambuser] Could not hydrate product', entry.url, error);
          });
      });
    });
  }

  // ---------------------------------------------------------------------------
  // Floating widget + accessibility fallback
  // ---------------------------------------------------------------------------

  var widgetPresent = false;
  var a11yEnabled = false;
  var userReopened = false;
  var showDismissed = false;

  function fabButton() {
    return document.getElementById('show-bambuser-fab');
  }

  function widgetEl() {
    return document.querySelector('[data-bambuser-liveshopping-floating-id]');
  }

  // No documented API to show/hide the FAB, so use the class.
  function setWidgetHidden(hidden) {
    var el = widgetEl();
    if (el) el.classList.toggle('hidden-by-close', hidden);
  }

  function readDismissedShow() {
    try {
      return window.localStorage.getItem(DISMISSED_SHOW_KEY);
    } catch (error) {
      return null;
    }
  }

  function writeDismissedShow(showId) {
    try {
      if (showId) window.localStorage.setItem(DISMISSED_SHOW_KEY, String(showId));
      else window.localStorage.removeItem(DISMISSED_SHOW_KEY);
    } catch (error) {
      debug('storage unavailable; dismissal will not persist');
    }
  }

  function readA11yMode() {
    var links = document.querySelectorAll('.UsableNetAssistive');
    for (var i = 0; i < links.length; i++) {
      if (links[i].offsetParent === null) continue;
      var text = links[i].textContent.trim();
      if (text.indexOf(A11Y_ACTIVE_TEXT) !== -1) return true;
      if (text.indexOf(A11Y_INACTIVE_TEXT) !== -1) return false;
    }
    return null;
  }

  function render() {
    var button = fabButton();
    if (!button) return;

    var hasEl = Boolean(widgetEl());
    var hideWidget = a11yEnabled && !userReopened;
    var show = widgetPresent && !showDismissed && hideWidget && hasEl;

    debug('render', {
      widgetPresent: widgetPresent,
      showDismissed: showDismissed,
      a11yEnabled: a11yEnabled,
      userReopened: userReopened,
      elementInDom: hasEl,
      button: show ? 'shown' : 'hidden',
    });

    button.style.display = show ? 'block' : 'none';
    if (widgetPresent) setWidgetHidden(showDismissed || hideWidget);
  }

  // Bambuser emits no event when the widget is torn down at show end.
  function watchWidgetRemoval() {
    var seen = Boolean(widgetEl());
    new MutationObserver(function () {
      var now = Boolean(widgetEl());
      if (now === seen) return;
      seen = now;
      debug('widget element', now ? 'added' : 'removed from DOM');
      if (!now) widgetPresent = false;
      render();
    }).observe(document.body, { childList: true });
  }

  function syncA11y() {
    var next = readA11yMode();
    if (next === null || next === a11yEnabled) return;
    debug('accessibility mode ->', next);
    a11yEnabled = next;
    if (!a11yEnabled) userReopened = false;
    render();
  }

  function initFab() {
    var button = fabButton();
    if (!button) return;

    button.addEventListener('click', function () {
      debug('fallback button clicked');
      userReopened = true;
      render();
      var el = widgetEl();
      if (el) {
        el.setAttribute('tabindex', '-1');
        el.focus({ preventScroll: true });
      }
    });

    document.addEventListener('click', function (event) {
      if (event.target.closest && event.target.closest('.UsableNetAssistive')) {
        requestAnimationFrame(syncA11y);
      }
    });

    var container = document.querySelector('.usable-net-container');
    if (container) {
      var frame = null;
      new MutationObserver(function () {
        if (frame) return;
        frame = requestAnimationFrame(function () {
          frame = null;
          syncA11y();
        });
      }).observe(container, { childList: true, subtree: true, characterData: true });
    }

    watchWidgetRemoval();
    syncA11y();
  }

  // ---------------------------------------------------------------------------
  // Entry points
  // ---------------------------------------------------------------------------

  function onPlayerReady(player) {
    var currency = getActiveCurrency() || 'USD';
    debug('player ready, currency =', currency);
    player.configure({
      currency: currency,
      locale: 'en-US',
      buttons: { dismiss: player.BUTTON.CLOSE },
    });
    bindCartHandlers(player);
    bindProductHydration(player);
  }

  function onWidgetReady(widget) {
    if (!widget || !widget.EVENT || !widget.EVENT.FLOATING) {
      debug('widget ready but EVENT.FLOATING missing', widget);
      return;
    }
    debug('widget ready');

    widget.on(widget.EVENT.FLOATING.PRESENT, function (event) {
      debug('FLOATING.PRESENT', event);
      // present:false means there is nothing to show.
      var showId = event && event.showId;
      widgetPresent = Boolean(event && event.present !== false && showId);

      var dismissed = readDismissedShow();
      if (showId && dismissed === String(showId)) {
        showDismissed = true;
        debug('show', showId, 'was dismissed previously');
      } else {
        showDismissed = false;
        if (dismissed) {
          debug('new show', showId, '- clearing dismissal of', dismissed);
          writeDismissedShow(null);
        }
      }
      render();
    });

    // Recorded shows only; live shows CONDENSE/EXPAND, which is not a dismissal.
    widget.on(widget.EVENT.FLOATING.DISMISS, function (event) {
      debug('FLOATING.DISMISS', event);
      var showId = event && event.showId;
      if (!showId) return;
      writeDismissedShow(showId);
      showDismissed = true;
      render();
    });
  }

  function start() {
    var players = window.__bambuserQueue || [];
    var widgets = window.__bambuserWidgetQueue || [];
    debug('start; queued players:', players.length, 'widgets:', widgets.length);

    initFab();

    window.__bambuserReady = onPlayerReady;
    window.__bambuserQueue = [];
    players.forEach(onPlayerReady);

    window.__bambuserWidgetReady = onWidgetReady;
    window.__bambuserWidgetQueue = [];
    widgets.forEach(onWidgetReady);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
